<template>
  <div>
    <div
      v-for="(productAttributeCategory, categoryIndex) in props.productAttributes"
      :key="'category' + categoryIndex"
      :class="bem('attribute-category')"
    >
      <div
        v-for="(
          attributeCategorySection, sectionIndex
        ) in productAttributeCategory.categorySections"
        :key="'section' + sectionIndex"
        :class="bem('attribute-section')"
      >
        <h6 :class="bem('attribute-section-title')">
          {{ attributeCategorySection.sectionHeading }}
          <ToolTip
            v-if="attributeCategorySection.sectionTooltip"
            arrow-position="left"
            :content="attributeCategorySection.sectionTooltip"
          />
        </h6>
        <div
          :class="
            bem('attribute-group', {
              gridType: getAttributeGridType(
                attributeCategorySection.attributes
              ),
            })
          "
        >
          <div
            v-for="(
              attribute, attributeIndex
            ) in attributeCategorySection.attributes"
            :key="'attribute' + attributeIndex"
            :class="bem('attribute')"
          >
            <VisionRatingScale
              v-if="attribute.type === 'dual_value_rating'"
              :rating-left="attribute.firstValue"
              :rating-right="attribute.secondValue"
              theme="gradient"
              colour="black"
              height="thin"
              :title-left="attribute.leftLabel"
              :title-center="attribute.middleLabel"
              :title-right="attribute.rightLabel"
            />
            <VisionRating
              v-else-if="attribute.type === 'single_value_rating'"
              :id="attribute.id"
              :rating="attribute.rating"
              theme="gradient"
              visual-style="line"
              colour="black"
              height="thin"
              :title="attribute.label"
            />
            <VisionRating
              v-else-if="attribute.type === 'circular_rating'"
              :id="attribute.id"
              :rating="attribute.rating"
              theme="gradient"
              visual-style="circle"
              colour="black"
              height="thin"
              :icon="attribute.icon?.icon"
              title="Rating"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CategoryAttributes } from '~/shared/types'

interface AccordionProductAttributes {
  productAttributes: Array<{
    categorySections: Array<{
      sectionHeading: string
      sectionTooltip: string
      attributes: CategoryAttributes
    }>
  }>
}

const props = defineProps<AccordionProductAttributes>()

const bem = useBEM('AccordionProductAttributes')

function getAttributeGridType(categoryAttributes: CategoryAttributes) {
  if (
    categoryAttributes.some(
      (categoryAttribute) =>
        categoryAttribute.type === 'circular_rating'
    )
  ) {
    return 'circles'
  } else if (
    categoryAttributes.some((categoryAttribute) =>
      ['dual_value_rating', 'single_value_rating'].includes(categoryAttribute.type)
    )
  ) {
    const barCount = categoryAttributes.filter(
      (categoryAttributes) =>
      ['dual_value_rating', 'single_value_rating'].includes(categoryAttributes.type)
    )

    if (barCount.length > 1) {
      return 'half-width-bars'
    }
  }

  return 'full-width-bars'
}
</script>

<style lang="postcss" scoped>
.AccordionProductAttributes__attribute-category {
  @apply flex flex-col gap-y-12;
}

.AccordionProductAttributes__attribute-section {
  @apply w-full;

  container: attribute-group / inline-size;
}

.AccordionProductAttributes__attribute-section-title {
  @apply flex items-center heading-6 mb-3;
}

.AccordionProductAttributes__attribute-group {
  @apply w-full;
}

.AccordionProductAttributes__attribute-group--grid-type-circles {
  @apply grid grid-cols-4 gap-3;

  @container attribute-group (min-width: 320px) {
    @apply grid-cols-5 gap-4;
  }

  @container attribute-group (min-width: 440px) {
    @apply grid-cols-6;
  }

  @container attribute-group (min-width: 740px) {
    @apply grid-cols-7;
  }
}

.AccordionProductAttributes__attribute-group--grid-type-half-width-bars {
  @apply grid grid-cols-1 gap-y-4;

  @container attribute-group (min-width: 320px) {
    @apply grid-cols-2 gap-6;
  }
}

.AccordionProductAttributes__attribute-group--grid-type-full-width-bars {
  @apply flex flex-col;
}

.AccordionProductAttributes__attribute {
  @apply w-full;
}
</style>
